import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, Link, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Helmet } from 'react-helmet';

import { useTheme } from '@mui/material/styles';

import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { Edit, Restore } from '@mui/icons-material';

import store from '../../../../store';
import { setRecenterMap, setZones } from '../../../../store/actions/Store';

import BackIcon from '../../../icons/BackIcon';
import BackArrowIcon from '../../../icons/BackArrowIcon';
import { LoadingScreen } from '../../../partials/LoadingScreen';
import OpenButton from '../../../partials/OpenButton';
import Main from '../../../styled/Main';
import DrawerHeader from '../../../styled/DrawerHeader';
import LeftSidePanel from '../map/LeftSidePanel';
import { VersionsDialog } from '../VersionsDialog';

import { getFloorPlanZones } from '../../../../services/zones';
import { setSideBarIsOpened } from '../../../../store/actions/Store';

const drawerWidth = 500;

export const handleZonesResponse = response => {
    if (response.data.success) {
        const zones = response.data?.floor_plan_areas || [];
        store.dispatch(setZones(zones));
    }
};

const EditCurrentStoreButton = ({ name, setVersionsDialogOpen, storeData }) => {
    const { floorPlanId } = useParams();

    if(!storeData) {
        return <CircularProgress />;
    }

    return (
        <Grid container alignItems="center" justifyContent="center" spacing={1}>
            <Grid item>
                <Typography>
                    {name}
                </Typography>
            </Grid>
            <Grid item>
                <Button
                    size="small"
                    variant="outlined"
                    LinkComponent={Link}
                    style={{maxWidth: '32px', maxHeight: '32px', minWidth: '32px', minHeight: '32px'}}
                    to={`/app/easy-floor/edit/${floorPlanId}`}
                >
                    <Edit />
                </Button>
            </Grid>
            {!!storeData?.floor_plan?.multiple_versions_enabled && (
                <Grid item>
                    <Button
                        size="small"
                        variant="outlined"
                        style={{maxWidth: '32px', maxHeight: '32px', minWidth: '32px', minHeight: '32px'}}
                        onClick={() => setVersionsDialogOpen(true)}
                    >
                        <Restore />
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};

function StoreMapContainer()
{
    const theme = useTheme();

    const [loading, setLoading] = useState(false);
    const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);

    const storeData = useSelector(state => state.store.storeData);
    const open = useSelector((state) => state.store.sideBarIsOpened);

    const { t } = useTranslation();

    const navigate = useNavigate();
    
    const st = storeData?.floor_plan || null; // store
    const stid = st?.id || null; // store id

    /*
    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'easyFloor'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps
    */

    const navigateToStores = () => {
        navigate('../easy-floor');
    };
    
    const handleVersionDialogClose = () => {
        setVersionsDialogOpen(false);
    }

    useEffect(() => {
        store.dispatch(setZones([])); // exampleZones
        if (stid) getFloorPlanZones(stid).then(handleZonesResponse);
    }, [stid]);

    const renderData = () => {
        return (
            <Box sx={{ display: 'flex', height: '100%' }}>
                <Helmet>
                    <title>{t("easyFloor")}</title>
                </Helmet>
                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            padding: '20px'
                        },
                        padding: '0px',
                        borderLeft: '1px solid ' + theme.palette.storeDrawer.borderLeft.color
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                    PaperProps={{ sx: {
                        padding: '0px',
                        position: "relative",
                        backgroundColor: theme.palette.storeDrawer.background
                    }}}
                >
                    <DrawerHeader>
                        <button
                            type="button"
                            onClick={navigateToStores}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                padding: '0',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <BackArrowIcon />
                            <span style={{ color: '#638CA3' }}>{t('Back')}</span>
                        </button>
                        <EditCurrentStoreButton
                            name={storeData?.floor_plan ? storeData.floor_plan.name + ' v' + storeData.floor_plan.number : ''}
                            setVersionsDialogOpen={setVersionsDialogOpen}
                            storeData={storeData}
                        />
                        <button
                            type="button"
                            onClick={() => { store.dispatch(setSideBarIsOpened(open ? false : true)); }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                padding: '0px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <BackIcon />
                        </button>
                    </DrawerHeader>
                    <LeftSidePanel />
                </Drawer>
                <Main open={open} drawerWidth={drawerWidth}>
                    <Outlet />
                </Main>
                <VersionsDialog
                    open={versionsDialogOpen}
                    handleClose={handleVersionDialogClose}
                    item={storeData ? storeData.floor_plan : null}
                />
            </Box>
        )
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    )
}

export default StoreMapContainer;
